// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, AppIcon, Button, IconButton, Spinner, TagChip, TextInput } from '@loa/ui-components';
import { useAdmin } from '../contexts/AdminContext';
import { useTags } from '../contexts/TagsContext';
import { tagsApi } from '../api/tagsApi';
import { fundsApi } from '../api/fundsApi';
import { TagFormModal } from '../components/tags/TagFormModal';
import type { TagFormValues } from '../components/tags/TagFormModal';
import { TagMergeModal } from '../components/tags/TagMergeModal';
import { TagDeleteModal } from '../components/tags/TagDeleteModal';
import type { TagDTO } from '../types/tag';
import './TagsAdminPage.css';

/**
 * Settings -> Tags (ADMIN-only). Table of tags (chip preview, name,
 * description, usage counts, archived) + create/edit/merge/delete
 * governance (Tags PRD §9). Usage counts merge the resource-server
 * `withUsage=true` total with funds-service's per-tag fund count (FR-15) so
 * the FUND column reflects the federated assignment surface.
 */
export default function TagsAdminPage() {
  const navigate = useNavigate();
  const { isAdmin, loading: adminLoading } = useAdmin();
  const { featureEnabled, refetch: refetchGlobalTags } = useTags();

  const [tags, setTags] = useState<TagDTO[]>([]);
  const [fundCounts, setFundCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const [formModal, setFormModal] = useState<{ open: boolean; tag?: TagDTO | null }>({ open: false });
  const [mergeModal, setMergeModal] = useState<{ open: boolean; tag: TagDTO | null }>({ open: false, tag: null });
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; tag: TagDTO | null }>({ open: false, tag: null });

  const loadTags = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await tagsApi.list({ includeArchived: true, withUsage: true });
      setTags(data);
      if (data.length > 0) {
        const fundUsage = await fundsApi.getFundTagUsage(data.map((t) => t.id)).catch(() => []);
        setFundCounts(Object.fromEntries(fundUsage.map((u) => [u.tagId, u.count])));
      } else {
        setFundCounts({});
      }
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status !== 404) {
        console.error('Failed to load tags:', err);
        setError('Failed to load tags. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!adminLoading && isAdmin) {
      loadTags();
    } else if (!adminLoading && !isAdmin) {
      navigate('/');
    }
  }, [adminLoading, isAdmin, loadTags, navigate]);

  const handleFormSubmit = async (values: TagFormValues) => {
    if (formModal.tag) {
      await tagsApi.update(formModal.tag.id, values);
    } else {
      await tagsApi.create({ name: values.name, color: values.color, description: values.description });
    }
    await loadTags();
    await refetchGlobalTags();
  };

  const handleMergeSuccess = async () => {
    await loadTags();
    await refetchGlobalTags();
  };

  const handleDeleteSuccess = async () => {
    await loadTags();
    await refetchGlobalTags();
  };

  const filteredTags = tags.filter((t) => t.name.toLowerCase().includes(search.trim().toLowerCase()));

  if (adminLoading || (loading && tags.length === 0 && !error)) {
    return (
      <div className="tags-admin-page">
        <Spinner />
      </div>
    );
  }

  if (!featureEnabled) {
    return (
      <div className="tags-admin-page">
        <Alert variant="info" header="Tags are not enabled">
          <p>The tags feature is not enabled for this brokerage.</p>
        </Alert>
      </div>
    );
  }

  return (
    <div className="tags-admin-page">
      <div className="tags-admin-page__header">
        <h1 className="tags-admin-page__title">
          Tags <span className="tags-admin-page__count">{tags.length}</span>
        </h1>
        <Button variant="filled" onClick={() => setFormModal({ open: true, tag: null })} iconLeft={<AppIcon name="plus" size={14} />}>
          Create tag
        </Button>
      </div>

      <div className="tags-admin-page__search">
        <TextInput
          iconLeft={<AppIcon name="search" size={16} color="#333333" />}
          placeholder="Search tags"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          fluid
        />
      </div>

      {error && (
        <Alert variant="error" header="Error Loading Tags">
          <p>{error}</p>
        </Alert>
      )}

      <div className="tags-admin-page__card">
        {filteredTags.length === 0 ? (
          <div className="tags-admin-page__empty">
            {tags.length === 0 ? 'No tags yet — create your first tag.' : 'No tags match your search.'}
          </div>
        ) : (
          <table className="tags-admin-table">
            <thead>
              <tr>
                <th>Tag</th>
                <th>Description</th>
                <th className="center">Usage (relationships/clients/policies/documents)</th>
                <th className="center">Funds</th>
                <th className="center">Total</th>
                <th className="center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTags.map((tag) => {
                const fundCount = fundCounts[tag.id] ?? 0;
                const totalCount = (tag.usageCount ?? 0) + fundCount;
                return (
                  <tr key={tag.id}>
                    <td>
                      <TagChip name={tag.name} color={tag.color} archived={tag.archived} />
                    </td>
                    <td className="tags-admin-table__description">{tag.description || <em>&mdash;</em>}</td>
                    <td className="center">{tag.usageCount ?? 0}</td>
                    <td className="center">{fundCount}</td>
                    <td className="center">{totalCount}</td>
                    <td className="tags-admin-table__actions">
                      <IconButton
                        variant="square-outlined"
                        size="sm"
                        onClick={() => setFormModal({ open: true, tag })}
                        icon={<AppIcon name="edit" size={14} />}
                        aria-label={`Edit ${tag.name}`}
                      />
                      <IconButton
                        variant="square-outlined"
                        size="sm"
                        onClick={() => setMergeModal({ open: true, tag })}
                        icon={<AppIcon name="exchange" size={14} />}
                        aria-label={`Merge ${tag.name}`}
                      />
                      <IconButton
                        variant="square-outlined"
                        size="sm"
                        onClick={() => setDeleteModal({ open: true, tag })}
                        icon={<AppIcon name="trash" size={14} />}
                        aria-label={`Delete ${tag.name}`}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <TagFormModal
        open={formModal.open}
        tag={formModal.tag}
        onClose={() => setFormModal({ open: false })}
        onSubmit={handleFormSubmit}
      />

      <TagMergeModal
        open={mergeModal.open}
        sourceTag={mergeModal.tag}
        candidates={tags.filter((t) => !t.archived && t.id !== mergeModal.tag?.id)}
        onClose={() => setMergeModal({ open: false, tag: null })}
        onSuccess={handleMergeSuccess}
      />

      <TagDeleteModal
        open={deleteModal.open}
        tag={deleteModal.tag}
        onClose={() => setDeleteModal({ open: false, tag: null })}
        onSuccess={handleDeleteSuccess}
      />
    </div>
  );
}
